<!--
  @component ProjectGridBlock — the full, searchable project set.
  tier:      home block (kind: "project-grid")
  purpose:   every project as a tile, with search — the "all" to the carousel's
             "recent". Lovely at 1 project and at 100.
-->
<script lang="ts">
  import { push } from "svelte-spa-router";
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useProvenanceIO } from "../../../../global/io/nodes/deployment/ProvenanceIO.svelte";
  import { useHomeActions } from "../home-context.js";
  import { projectRollup } from "../home-util.js";
  import ProjectTile from "../ProjectTile.svelte";

  const identity = useIdentityIO();
  const provenance = useProvenanceIO();
  const actions = useHomeActions();

  let q = $state("");
  const roll = $derived(projectRollup(provenance.events));
  const filtered = $derived.by(() => {
    const list = [...identity.projects].sort((a, b) => (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0));
    const s = q.trim().toLowerCase();
    return s ? list.filter((p) => p.name.toLowerCase().includes(s)) : list;
  });
</script>

<section class="block">
  <header class="bh">
    <h2>All projects</h2>
    <input class="search" type="text" placeholder="Search projects…" bind:value={q} spellcheck="false" />
  </header>

  {#if filtered.length === 0}
    <div class="empty">{q ? `No projects match “${q}”.` : "No projects yet."}</div>
  {:else}
    <div class="grid">
      {#each filtered as p (p.id)}
        {@const r = roll.get(p.id)}
        <ProjectTile
          project={p}
          status={r?.status ?? "idle"}
          lastActivityMs={r?.lastActivityMs}
          onClick={() => push(`/projects/${p.id}/overview`)}
        />
      {/each}
      <button type="button" class="add" onclick={() => actions.openNewProject()}>
        <span class="add-plus" aria-hidden="true">+</span>
        <span>New project</span>
      </button>
    </div>
  {/if}
</section>

<style>
  .block { display: flex; flex-direction: column; gap: var(--space-4); font-family: var(--type-font); }
  .bh { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); }
  .bh h2 { margin: 0; font-size: 15px; font-weight: 600; letter-spacing: -0.01em; color: var(--text-primary); }
  .search {
    width: 220px; max-width: 48%; padding: 7px var(--space-3);
    background: var(--surface-sunken); border: 0.5px solid var(--border); border-radius: var(--radius-md);
    color: var(--text-primary); font: inherit; font-size: 12.5px;
  }
  .search::placeholder { color: var(--text-ghost); }
  .search:focus { outline: none; border-color: var(--border-focus); }

  .empty { font-size: 13px; color: var(--text-tertiary); padding: var(--space-5) 0; }

  .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(248px, 1fr)); gap: var(--space-4); }
  .add {
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px;
    min-height: 188px;
    background: transparent; border: 1px dashed var(--border-strong); border-radius: var(--radius-lg, 14px);
    color: var(--text-tertiary); font: inherit; font-size: 13px; font-weight: 500; cursor: pointer;
    transition: color var(--ease-fast), border-color var(--ease-fast), background var(--ease-fast);
  }
  .add:hover { color: var(--text-primary); border-color: var(--border-focus); background: var(--bg-hover); }
  .add-plus { font-size: 26px; font-weight: 300; line-height: 1; }
</style>
